/**
 * BRANDING POR PRODUCTO (favicon + título + theme-color), en runtime.
 *
 * El build es único y el producto se detecta por hostname (lib/productoActivo), así que
 * el `index.html` no puede traer el ícono correcto horneado. Acá, en el arranque, se pinta
 * el ícono del producto activo: MISMO template del tile (rect redondeado con gradiente +
 * las 2 letras en blanco), cambiando solo el color y las letras. Escala a ni/li/di
 * agregando una entrada al mapa — sin tocar más nada.
 *
 * ai queda idéntico al `favicon.svg` de siempre. Los colores de cada producto no se repiten
 * (decisión de familia con Sebastián): ai azul, ci magenta; ni/li/di se agregan cuando salgan.
 */
import { type ProductoOpus, productoActivo } from './productoActivo.js';

interface BrandingProducto {
  letras: string;
  nombre: string;
  /** Gradiente del tile: c1 (arriba-izq) → c2 (abajo-der). */
  c1: string;
  c2: string;
  descripcion: string;
}

const BRANDING: Record<ProductoOpus, BrandingProducto> = {
  ai: {
    letras: 'ai',
    nombre: 'OPUS ai',
    c1: '#0c6fd6',
    c2: '#0e4485',
    descripcion: 'OPUS ai — asistente IA para licitaciones y cotización de obra',
  },
  ci: {
    letras: 'ci',
    nombre: 'OPUS ci',
    c1: '#D6299B',
    c2: '#A11473',
    descripcion: 'OPUS ci — de los planos a la hoja de presupuesto',
  },
};

/** SVG del tile del producto (mismo diseño que public/favicon.svg). */
function faviconSvg(b: BrandingProducto): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
    `<defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">` +
    `<stop offset="0%" stop-color="${b.c1}"/><stop offset="100%" stop-color="${b.c2}"/>` +
    `</linearGradient></defs>` +
    `<rect width="32" height="32" rx="6" fill="url(#g)"/>` +
    `<text x="16" y="22" text-anchor="middle" font-family="Space Grotesk, -apple-system, sans-serif" ` +
    `font-weight="700" font-size="15" fill="#fff">${b.letras}</text>` +
    `</svg>`
  );
}

function setMeta(name: string, content: string): void {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Pinta el branding del producto activo. Idempotente: se puede llamar en cada arranque.
 * No corre en modo DEMO (ahí el título lo fija el propio demo).
 */
/** Nombre visible del producto activo ("OPUS ai" | "OPUS ci"), para textos de UI. */
export function nombreProductoActivo(producto: ProductoOpus = productoActivo()): string {
  return BRANDING[producto].nombre;
}

/**
 * Branding del producto activo (letras, nombre, colores del tile). Única fuente:
 * el mismo mapa que pinta el favicon, para que el logo del topbar y el ícono del
 * navegador no se contradigan.
 */
export function brandingProductoActivo(producto: ProductoOpus = productoActivo()): {
  producto: ProductoOpus; letras: string; nombre: string; c1: string; c2: string;
} {
  return { producto, ...BRANDING[producto] };
}

export function aplicarBrandingDelProducto(producto: ProductoOpus = productoActivo()): void {
  const b = BRANDING[producto];
  document.title = b.nombre;
  setMeta('theme-color', b.c1);
  setMeta('description', b.descripcion);

  const href = `data:image/svg+xml,${encodeURIComponent(faviconSvg(b))}`;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'icon');
    document.head.appendChild(link);
  }
  link.setAttribute('type', 'image/svg+xml');
  link.setAttribute('href', href);
}
